/**
 * MCPAppRenderer - Renders MCP App UI resources in sandboxed iframes
 *
 * Renders interactive HTML visualizations (shot charts, data explorers, etc.)
 * inside sandboxed iframes. Handles resource fetching, data injection via
 * postMessage, and server tool call proxying.
 *
 * @see https://github.com/MCP-UI-Org/mcp-ui
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { C } from '../design-system/primitives/constants';
import config from '../../utils/config';

const API_BASE = config.API_BASE_URL || '';

const detectContentType = (text) => {
  const head = (text || '').trimStart().slice(0, 200).toLowerCase();
  if (head.startsWith('<!doctype') || head.startsWith('<html') || head.startsWith('<body')) {
    return 'html';
  }
  return 'markdown';
};

export default function MCPAppRenderer({
  resourceUri,
  toolName,
  toolInput = {},
  toolResult = {},
  height = 400,
  onAction,
  className = '',
}) {
  const [state, setState] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [contentType, setContentType] = useState('html');
  const iframeRef = useRef(null);

  // Fetch the UI resource from the backend
  useEffect(() => {
    if (!resourceUri) {
      setState('error');
      setErrorMessage('No resource URI provided');
      return;
    }

    const fetchResource = async () => {
      setState('loading');
      try {
        const path = resourceUri.replace('ui://', '');
        const response = await fetch(`${API_BASE}/mcp/resources/${path}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch resource: ${response.status}`);
        }

        const data = await response.json();
        const resource = data.resource || data;

        const raw = resource.text ?? (resource.blob ? atob(resource.blob) : null);
        if (raw == null) {
          throw new Error('Resource has no content');
        }
        setContentType(detectContentType(raw));
        setHtmlContent(raw);
        setState('ready');
      } catch (err) {
        console.error('[MCPAppRenderer] Fetch error:', err);
        setState('error');
        setErrorMessage(err.message);
      }
    };

    fetchResource();
  }, [resourceUri]);

  // Send tool data to iframe once it loads
  const handleIframeLoad = useCallback(() => {
    if (!iframeRef.current?.contentWindow) return;

    iframeRef.current.contentWindow.postMessage({
      type: 'toolResult',
      toolName,
      toolInput,
      toolResult,
      theme: {
        bg: C.bg,
        surface1: C.s1,
        surface2: C.s2,
        surface3: C.s3,
        gold: C.gold,
        goldDim: C.goldDim,
        text: C.text,
        textSecondary: C.t2,
        textTertiary: C.t3,
        border: C.border,
        borderLight: C.borderLight,
      },
    }, '*');
  }, [toolName, toolInput, toolResult]);

  // Listen for messages from the iframe
  useEffect(() => {
    const handleMessage = async (event) => {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;

      const { type, payload } = event.data || {};

      switch (type) {
        case 'tool': {
          try {
            const response = await fetch(`${API_BASE}/mcp/execute`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                server: 'slam-analytics',
                tool: payload.toolName,
                params: payload.params,
              }),
            });
            const result = await response.json();
            iframeRef.current?.contentWindow?.postMessage({
              type: 'toolCallResult',
              toolName: payload.toolName,
              result,
            }, '*');
          } catch (err) {
            iframeRef.current?.contentWindow?.postMessage({
              type: 'toolCallError',
              toolName: payload.toolName,
              error: err.message,
            }, '*');
          }
          break;
        }

        case 'link':
          window.open(payload.url, '_blank', 'noopener,noreferrer');
          break;

        case 'notify':
          console.log('[MCPApp]', payload.message);
          break;

        default:
          break;
      }

      onAction?.({ type, payload });
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onAction]);

  if (state === 'loading') {
    return (
      <div
        className={`flex items-center justify-center rounded-lg ${className}`}
        style={{ height, background: C.s1, border: `1px solid ${C.border}` }}
      >
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: C.gold }} />
          <span className="text-xs" style={{ color: C.t3 }}>Loading visualization...</span>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div
        className={`flex items-center justify-center rounded-lg ${className}`}
        style={{ height: 120, background: `${C.dev}10`, border: `1px solid ${C.dev}30` }}
      >
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4" style={{ color: C.dev }} />
          <span className="text-xs" style={{ color: C.dev }}>
            {errorMessage || 'Failed to load visualization'}
          </span>
        </div>
      </div>
    );
  }

  if (contentType === 'markdown') {
    return (
      <div
        className={`relative rounded-lg overflow-auto ${className}`}
        style={{ border: `1px solid ${C.border}`, background: C.bg, maxHeight: height, padding: '12px 16px' }}
      >
        <div
          className="prose prose-sm prose-invert max-w-none [&_p]:text-xs [&_li]:text-xs [&_h1]:text-sm [&_h2]:text-xs [&_h3]:text-xs [&_code]:text-[11px] [&_pre]:text-[11px]"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(htmlContent)) }}
        />
      </div>
    );
  }

  return (
    <div className={`relative rounded-lg overflow-hidden ${className}`} style={{ border: `1px solid ${C.border}` }}>
      <iframe
        ref={iframeRef}
        title="MCP App"
        srcDoc={htmlContent}
        onLoad={handleIframeLoad}
        sandbox="allow-scripts allow-same-origin"
        style={{
          width: '100%',
          height,
          border: 'none',
          background: C.bg,
        }}
      />
    </div>
  );
}
